/**
 * Coordinate transformation utilities for visualization
 * Pure functions for calculating domains and D3 scales
 */

import * as d3 from 'd3';
import type { Polygon } from 'src/types';

export interface Domains {
  xDomain: [number, number];
  yDomain: [number, number];
}

export interface Scales {
  xScale: d3.ScaleLinear<number, number>;
  yScale: d3.ScaleLinear<number, number>;
}

/**
 * Find minimum and maximum coordinates from polygons and points
 * Handles special BoundingBox polygons to define viewport limits
 *
 * @param polygons - Array of polygons (Struktur, Attika, BoundingBox)
 * @param points - Array of measurement points
 * @returns Object with xDomain and yDomain arrays
 */
export function findMinMaxCoordinates(polygons: Polygon[], points: any[]): Domains {
  const paddingFactor = 0.1;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  // Find bounds from all polygons
  polygons.forEach((polygon) => {
    polygon.coordinates.forEach((point) => {
      const [x, y] = point;
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    });
  });

  // Include measurement points in bounds
  points.forEach((point) => {
    // Validate point has x and y properties and they are numbers
    if (point.x === undefined || point.y === undefined || isNaN(point.x) || isNaN(point.y)) {
      if (import.meta.env.DEV) {
        console.warn('Invalid point detected in coordinate transform:', point);
      }
      return; // Skip this point
    }

    const x = point.x;
    const y = point.y;
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  });

  // Check for BoundingBox polygons (override default bounds)
  const boundingBoxPolys = polygons.filter((p) => p.description === 'BoundingBox');

  if (boundingBoxPolys.length > 0) {
    // Reset bounds to use only BoundingBox polygons
    minX = Infinity;
    minY = Infinity;
    maxX = -Infinity;
    maxY = -Infinity;

    boundingBoxPolys.forEach((polygon) => {
      polygon.coordinates.forEach((point) => {
        const [x, y] = point;
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      });
    });
  }

  // Apply padding to domains
  const dx = maxX - minX;
  const dy = maxY - minY;
  const xDomain: [number, number] = [minX - paddingFactor * dx, maxX + paddingFactor * dx];
  const yDomain: [number, number] = [minY - paddingFactor * dy, maxY + paddingFactor * dy];

  return { xDomain, yDomain };
}

/**
 * Create D3 scales with proper aspect ratio handling
 * Ensures the visualization maintains correct proportions
 *
 * @param domains - The x and y domains from findMinMaxCoordinates
 * @param width - Canvas width in pixels
 * @param height - Canvas height in pixels
 * @returns Object with xScale and yScale D3 linear scales
 */
export function getScales(domains: Domains, width: number, height: number): Scales {
  const dx_domain = domains.xDomain[1] - domains.xDomain[0];
  const dy_domain = domains.yDomain[1] - domains.yDomain[0];
  const aspect_domain = dy_domain / dx_domain;
  const aspect_canvas = height / width;

  let xScale: d3.ScaleLinear<number, number>;
  let yScale: d3.ScaleLinear<number, number>;

  if (aspect_domain > aspect_canvas) {
    // Domain is taller than canvas - fit to height
    const xrange = height / aspect_domain;
    const slack = dy_domain / aspect_canvas - dx_domain;

    // Center horizontally by adjusting x domain
    const adjustedXDomain: [number, number] = [
      domains.xDomain[0] - slack / 2,
      domains.xDomain[1] + slack / 2,
    ];

    xScale = d3.scaleLinear().domain(adjustedXDomain).range([0, xrange]);

    yScale = d3.scaleLinear().domain(domains.yDomain).range([height, 0]);
  } else {
    // Domain is wider than canvas - fit to width
    xScale = d3.scaleLinear().domain(domains.xDomain).range([0, width]);

    yScale = d3
      .scaleLinear()
      .domain(domains.yDomain)
      .range([width * aspect_domain, 0]);
  }

  return { xScale, yScale };
}
